import { ReactMediaRecorder } from "react-media-recorder";
import { useEffect, useRef, useState } from "react";
import RecorderUIInner from "./recorder-ui-inner";

interface AudioRecorderUIProps {
  onFinished: (finished: boolean) => void;
  onRecordingComplete?: (blobUrl: string) => void;
}

const AudioRecorderUI: React.FC<AudioRecorderUIProps> = ({
  onFinished,
  onRecordingComplete,
}) => {
  const [autoStarted, setAutoStarted] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [finished, setFinished] = useState(false);
  const audioStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    onFinished(finished);
  }, [finished, onFinished]);

  useEffect(() => {
    return (): void => {
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
        audioStreamRef.current = null;
      }
    };
  }, []);

  return (
    <ReactMediaRecorder
      stopStreamsOnStop
      askPermissionOnMount={false}
      audio={{
        autoGainControl: true,
        echoCancellation: true,
        noiseSuppression: true,
        sampleRate: 22050,
      }}
      mediaRecorderOptions={{
        mimeType: "audio/webm; codecs=opus",
        audioBitsPerSecond: 16000,
      }}
      render={({
        status,
        startRecording,
        stopRecording,
        pauseRecording,
        resumeRecording,
        mediaBlobUrl,
        previewStream,
        previewAudioStream,
      }) => {
        if (!audioStreamRef.current && previewAudioStream) {
          audioStreamRef.current = previewAudioStream;
        }
        if (status === "stopped" && mediaBlobUrl && onRecordingComplete) {
          onRecordingComplete(mediaBlobUrl);
        }
        return (
          <RecorderUIInner
            autoStarted={autoStarted}
            finished={finished}
            mediaBlobUrl={mediaBlobUrl ?? null}
            mediaStream={finished ? previewStream : previewAudioStream}
            pauseRecording={pauseRecording}
            recordingTime={recordingTime}
            resumeRecording={resumeRecording}
            setAutoStarted={setAutoStarted}
            setFinished={setFinished}
            setRecordingTime={setRecordingTime}
            setShowConfirmation={setShowConfirmation}
            showConfirmation={showConfirmation}
            startRecording={startRecording}
            status={status}
            stopRecording={stopRecording}
          />
        );
      }}
    />
  );
};

export default AudioRecorderUI;
